import { clone, padStart } from 'lodash-es'
import { fabric } from 'fabric'
import { findParentPath } from '../../utils'
import type { IMerchTemplate, IMerchTemplateFileMapping, IMerchTemplateOption } from './IMerchTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { merchConstants } from '@/models/constants'
import utils from '@/services/utils'
import type MyArticle from '@/models/myArticle'
import type Merch from '@/modules/merch/services/merch'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import { getArticlesAssets } from '@/api/t1/article'
import type DuneAsset from '@/models/duneAsset'
import uaStandardImage from '@/assets/templateLayoutImages/uaStandard.png'

class UAStandard implements IMerchTemplate {
  id = merchConstants.slideGenTemplatesId.uaStandard
  name = 'Under Armour - Standard'
  layoutImage = uaStandardImage
  slideWidth: number = 1280
  slideHeight: number = 720
  imageHeight: number = 500
  imageWidth: number = 500
  font: string = 'Arial'
  imageDefaultScaleFactor: number = merchConstants.slideImageDefaultScaleFactors.large

  remainingHeight: number = 0
  maxNumberOfImagePerRow: number = 6
  imgSize: number = 115
  imgHorSpacing: number = 10
  imgVerSpacing: number = 10
  labelHeightPerLine: number = 20
  headerHeight: number = 40
  modelInfoWidth: number = 505
  thumbnailProps = ['ColorwayCode', 'Colorway']
  infoTableColorwayCodeWidth: number = 25
  infoTableColorwayWidth: number = 275
  infoTableStartShipWidth: number = 70
  infoTableEndShipWidth: number = 70
  infoTableHardLaunchDateWidth: number = 80
  infoTableMarkdownDateWidth: number = 80
  logoWidth: number = 77
  logoHeight: number = 64
  modelInfoHeight: number = 0
  maxThumbnailHeight: number = 0
  maxVRThumbnailHeight: number = 0
  lowestThumbnailBottomPositionInModel: number = 0 // it will  maintain the  bottom position of the thumbnails of the last row in each model
  modelNumberProp = {
    fontSize: 10,
    fontWeight: 'normal',
    width: 500,
    height: 0,
  }

  articleNameProp = {
    fontSize: 14,
    fontWeight: 'bold',
    width: 500,
    height: 0,
  }

  priceAndFabricProp = {
    fontSize: 12,
    fontWeight: 'normal',
    width: 500,
    priceWidth: 0,
    height: 0,
  }

  featureAndBenefitsProp = {
    fontSize: 10,
    fontWeight: 'normal',
    width: 500,
    height: 0,
    marginTop: 5,
    marginBottom: 5,
  }

  platformProp = {
    fontSize: 10,
    fontWeight: 'normal',
    width: 1280 - 505,
    height: 0,
  }

  periodsHeaderProp = {
    font: this.font,
    fontSize: 16,
    fontWeight: 'normal',
    width: 0,
    height: this.labelHeightPerLine,
    marginTop: 5,
  }

  periodColumnsInfo = {
    c1: {
      width: 0, // will be calculated dynamically based on number of periods
      height: 0,
      maxThumbnailInARow: -1, // set dynamically either 6 or 4 or 3
      maxTrailingThumbnailsFromPreviousColumns: 0,
      dividerTopPosition: 0, // uses to Draw the vertical lines to divide columns. As needed we will add lines at the end so that can be drawn till the data is there
      dividerLeftPosition: 0,
      dividerHeight: 0,
    },
    c2: {
      width: 2 * (this.imgSize + this.imgHorSpacing), // draw vertical line in middle of imgHorSpacing
      height: 0,
      maxThumbnailInARow: 2,
      maxTrailingThumbnailsFromPreviousColumns: -1, // set dynamically either 3 or 4
      dividerTopPosition: 0,
      dividerLeftPosition: 0,
      dividerHeight: 0,
      dividerTop: 0,
      dividerLeft: 0,
    },
    c3: {
      width: this.imgSize + this.imgHorSpacing,
      height: 0,
      maxThumbnailInARow: 1,
      maxTrailingThumbnailsFromPreviousColumns: 5,
      dividerTopPosition: 0,
      dividerLeftPosition: 0,
      dividerHeight: 0,
      dividerTop: 0,
      dividerLeft: 0,
    },
  }

  vrImagesProp = {
    height: 300,
    width: 300,
    maxImagesPerRow: 4,
  }

  includeMarkdownDate: boolean = false
  includeHardLaunchDate: boolean = false
  hardLaunchDateAttribute: string = ''
  ShipmentStartDisplayLabel: string = 'Ship Start'
  ShipmentStartDateAttribute: string = 'ShipmentStartDate'
  ShipmentEndDateAttribute: string = 'ShipmentEndDate'
  includeUAStandardLaunchDateAttribute: boolean = false
  periodsMap = new Map() // map of lowercase period to on an object {value: [actual value], articleId, index: [index of period value after sorting]}, (based on business requirement max of 3 period will be considered for grouping)
  vrImagesCount: number = 0 // used to add in the count of images to download for showing progress
  isMerch: boolean = true

  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>): IMerchTemplateOption[] {
    const attributes: IMyAttribute[] = []
    for (const key in myAttributes) {
      attributes.push(myAttributes[key])
    }

    // TODO: Implement configurable options
    const options: IMerchTemplateOption[] = []

    options.push({
      name: 'sortByExcelSequence',
      label: 'generateDialog.steps.options.sortByExcelSequence',
      type: 'bool',
      default: false,
      required: false,
    })

    options.push({
      name: 'sortByListSequence',
      label: 'generateDialog.steps.options.sortByListSequence',
      type: 'bool',
      default: false,
      required: false,
    })

    options.push({
      name: 'groupByPeriod',
      label: 'generateDialog.steps.options.groupByPeriod',
      type: 'bool',
      default: false,
      required: false,
    })

    options.push({
      name: 'isOneModelPerPage',
      label: 'generateDialog.steps.options.oneModelPerPage',
      type: 'bool',
      default: false,
      required: false,
    })
    options.push({
      name: 'includeAllVRAngles',
      label: 'generateDialog.steps.options.includeAllVRAngles',
      type: 'bool',
      default: false,
      disabled: true,
      required: false,
    })
    options.push({
      name: 'imageType',
      label: 'generateFrameDialog.steps.options.imageType',
      type: 'list',
      default: 'firstImage',
      data: merchConstants.defaultImageType,
      required: false,
      multiple: false,
      valueProp: 'key',
      displayProp: 'value',
    })

    return options
  }

  getFileMapping(): IMerchTemplateFileMapping[] {
    const mapping: IMerchTemplateFileMapping[] = []

    mapping.push({
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'colorwaynumber', 'colorway number', 'style color code'],
    })
    mapping.push({
      name: 'articlePrice',
      column: 'ArticlePrice',
      type: 'list',
      label: 'generateFrameDialog.steps.mapping.articlePrice',
      required: false,
      autoMap: [],
    })
    return mapping
  }

  async generate(catalog: CatalogDetails, articlesData: MyArticle[], options: Record<string, any>, merch: Merch | undefined, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, excelData?: Record<string, Record<string, string[]>>, retailPg?: CatalogPriceGroup): Promise<(void | number)> {
    const data: IArticleData = { articles: [] }
    let isCustomArticlePrice: boolean = false
    if (utils.isDefined(excelData)) {
      data.articlePrices = {}
      articlesData.forEach((article) => {
        data.articles.push(article)
        data.articlePrices![article.ArticleNumber] = utils.isDefined(excelData[article.ArticleNumber].articlePrice) && excelData[article.ArticleNumber].articlePrice[0] ? Number(excelData[article.ArticleNumber].articlePrice[0]) : 0
        if (!isCustomArticlePrice && utils.isDefined(excelData[article.ArticleNumber].articlePrice) && excelData[article.ArticleNumber].articlePrice.length) {
          isCustomArticlePrice = true
        }
      })
    }
    else {
      articlesData.forEach((article) => {
        data.articles.push(article)
      })
    }
    const assetMapForVRImages: Record<string, DuneAsset[]> = {}
    if (options.isOneModelPerPage && options.includeAllVRAngles) {
      const res = await utils.tryAsync(getArticlesAssets(catalog.DuneContext, catalog.ContextKey, data.articles.map(s => s.ArticleNumber)))
      if (res.success && res.result.data && res.result.data.length) {
        const assets = res.result.data
        if (assets && assets.length > 0) {
          assets.forEach((asset) => {
            if (!assetMapForVRImages[asset.ImageSet]) {
              assetMapForVRImages[asset.ImageSet] = [asset]
            }
            else {
              utils.insertSorted(asset, assetMapForVRImages[asset.ImageSet], (a, b) => utils.comparer(a, b, ['SortOrder']))
            }
          })
        }
      }
    }
    return await this.buildTemplate(catalog, data, assetMapForVRImages, isCustomArticlePrice, options, merch, myAttributes, retailPg)
  }

  buildTemplate(catalog: CatalogDetails, articlesData: IArticleData, assetMapForVRImages: Record<string, DuneAsset[]>, isCustomArticlePrice: boolean, options: Record<string, any>, merch: Merch | undefined, myAttributes: Record<string, IMyAttribute>, retailPg?: CatalogPriceGroup) {
    this.isMerch = true
    const showPriceThousandsSeparated = catalog?.Config.ShowPriceThousandsSeparated
    const promises: Promise<void | number>[] = []
    const currentObjects: any[] = []
    if (utils.isDefined(merch)) {
      this.isMerch = true
    }
    else {
      this.isMerch = false
    }
    this.maxThumbnailHeight = this.getMaxThumbnailHeight()
    this.maxVRThumbnailHeight = this.getMaxVRThumbnailHeight()
    this.vrImagesCount = 0
    const props: IProp = {
      maxImagePerRow: this.maxNumberOfImagePerRow,
      slideNumber: 0,
      currentRowMaxLabelHeight: this.labelHeightPerLine * this.thumbnailProps.length,
      currentTopPosition: this.headerHeight, // not in use when groupByPeriod is true
      infoTableTopPosition: -1,
      highlight: 1,
      articleName: '',
      groupByPeriodColumnTracker: {},
      groupByPeriodOverflowedArticles: [],
      previousModelColumns: [],
      articleIdList: [],
    }
    const allSlides = {}
    let articleThumbnailObjects: any[] = []
    const headerGroups = this.generateGroups(catalog, articlesData, isCustomArticlePrice, options.sortByExcelSequence, options.sortByListSequence, options.groupByPeriod, myAttributes, retailPg)

    headerGroups.forEach((headerGroup) => {
      // new page
      this.onNewPage(currentObjects, articleThumbnailObjects, headerGroup, props)
      props.previousModelColumns = []
      Array.from(headerGroup.modelGroups.entries()).forEach((data, index) => {
        const dataValue = data as Array<any>
        const modelNumber = dataValue[0]
        const model = dataValue[1]
        const modelProps = {
          modelPeriodsMap: new Map(),
        }
        if (options.groupByPeriod) {
          // For group by period create modelPeriodMap
          let indexMap = 0
          this.periodsMap.forEach((value, key) => {
            if (model.columnsRequired.includes(key)) {
              value.index = indexMap
              modelProps.modelPeriodsMap.set(key, value)
              indexMap++
            }
          })
          // If previous model period Columns is not same as current period columns then move to next page
          if ((modelProps.modelPeriodsMap.size > 0 && !this.isTwoArraysSame(props.previousModelColumns, model.columnsRequired)) || options.isOneModelPerPage) {
            if (index !== 0) {
              // create the page by saving the canvas or creating objects
              if (this.isMerch) {
                promises.push(this.createSlide(merch, props, currentObjects.slice(), options)
                  .catch((error) => {
                    // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
                    console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
                  }))
              }
              this.onNewPage(currentObjects, articleThumbnailObjects, headerGroup, props)
            }
            this.periodOnNewPage(modelProps.modelPeriodsMap, props, options, currentObjects)
          }
          else {
            // if not moving to another page , need to reset the lowestThumbnailBottomPositionInModel for all the Periods in Map
            Array.from(modelProps.modelPeriodsMap).forEach((entry, index) => {
              props.groupByPeriodColumnTracker[index].lowestThumbnailBottomPositionInModel = 0
            })
          }
        }
        const modelInfoHeightProp = {
          cwAndColorwayHeader: 15,
          margin: 10,
          cwDataHeight: 0,
        }
        this.modelInfoHeight = 0
        this.lowestThumbnailBottomPositionInModel = 0

        this.modelInfoHeight = this.modelInfoAndTableHeaderHeight(model, modelInfoHeightProp, isCustomArticlePrice, showPriceThousandsSeparated)

        // calculate min height a row need to be rendered
        const maxHeightOneRowCanTake = Math.max(this.maxThumbnailHeight, (this.modelInfoHeight + this.calculateColorwayTableContentHeightForOneRow(model.articles.length)))

        // NEW PAGE
        // if remaining height is not enough for spaces needed for single row or isOneModelPerPage is selected by user and its not the first model => create slide/object then call onNewPage
        if (maxHeightOneRowCanTake > this.remainingHeight || (options.isOneModelPerPage && index !== 0 && !options.groupByPeriod)) {
          // this one doenst work properly as right now you will not figure out position of articles and height for vertical dividers
          // currentObjects.push(...this.createVerticalPeriodDividerLines(modelProps.modelPeriodsMap))

          // create the page by saving the canvas or creating objects
          if (this.isMerch) {
            promises.push(this.createSlide(merch, props, currentObjects.slice(), options)
              .catch((error) => {
                // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
                console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
              }))
          }

          this.onNewPage(currentObjects, articleThumbnailObjects, headerGroup, props)
          // if group by period
          if (options.groupByPeriod && modelProps.modelPeriodsMap.size > 0) {
            this.periodOnNewPage(modelProps.modelPeriodsMap, props, options, currentObjects)
          }
        }

        // new model new line
        this.onNewRow(props, options, modelProps.modelPeriodsMap)
        currentObjects.push(...this.addModelInfoAndTableHeader(model, props, modelProps.modelPeriodsMap, options, modelNumber, isCustomArticlePrice, true, showPriceThousandsSeparated))

        // let cwDataHeight = 0 moved to modelInfoHeightProp.cwDataHeight
        props.articleName = model.modelInfo.articleName
        // in case of excel sort all articles in each model by Article Number
        if (options.sortByExcelSequence || options.sortByListSequence) {
          utils.sort(model.articles, ['ArticleNumber'])
        }

        if (options.groupByPeriod && modelProps.modelPeriodsMap.size) {
          for (const columnIndex in props.groupByPeriodColumnTracker) {
            props.groupByPeriodColumnTracker[columnIndex].currentTopPosition += this.platformProp.height
          }
        }
        else {
          props.currentTopPosition += this.platformProp.height
        }
        model.articles.forEach((article, index) => {
          if (!(options.groupByPeriod && modelProps.modelPeriodsMap.size) && index % props.maxImagePerRow === 0 && index !== 0) {
            props.currentTopPosition += this.imgSize + this.imgVerSpacing + props.currentRowMaxLabelHeight
            // TODO:#TP update the currentTopPosition in groupByPeriodColumnTracker when group by period
            let newPageFlag = false
            const isEnoughSpaceForNextRow = this.slideHeight - (props.currentTopPosition + this.maxThumbnailHeight) > 0
            if (!isEnoughSpaceForNextRow) {
              // NEW PAGE
              // create the page by saving the canvas or creating objects
              currentObjects.push(...this.createVerticalPeriodDividerLines(modelProps.modelPeriodsMap, props.currentTopPosition))
              newPageFlag = true
              if (this.isMerch) {
                promises.push(this.createSlide(merch, props, currentObjects.slice(), options)
                  .catch((error) => {
                    // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
                    console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
                  }))
              }
              modelInfoHeightProp.cwDataHeight = 0
              this.onNewPage(currentObjects, articleThumbnailObjects, headerGroup, props)
              // if group by period
              if (options.groupByPeriod && modelProps.modelPeriodsMap.size > 0) {
                this.periodOnNewPage(modelProps.modelPeriodsMap, props, options, currentObjects)
              }
            }
            this.onNewRow(props, options, modelProps.modelPeriodsMap)
            if (newPageFlag) {
              // add model Info and table header
              currentObjects.push(...this.addModelInfoAndTableHeader(model, props, modelProps.modelPeriodsMap, options, modelNumber, isCustomArticlePrice, false, showPriceThousandsSeparated))
              newPageFlag = false
            }
          }
          articleThumbnailObjects = this.addArticle(article, index, options, props, modelInfoHeightProp, modelProps.modelPeriodsMap)
          currentObjects.push(...articleThumbnailObjects)
          // height issue
          if (model.articles.length === 1) {
            modelInfoHeightProp.cwDataHeight += 5
          }
        })
        // MODEL'S ARTICLES LOOP END

        // update RemainingHeight
        const currentMaxHeight = (Math.max(this.lowestThumbnailBottomPositionInModel, (this.modelInfoHeight + modelInfoHeightProp.cwDataHeight))) + 7
        this.remainingHeight -= currentMaxHeight
        if (options.groupByPeriod && modelProps.modelPeriodsMap.size) {
          for (const columnIndex in props.groupByPeriodColumnTracker) {
            props.groupByPeriodColumnTracker[columnIndex].currentTopPosition = this.slideHeight - this.remainingHeight
          }
        }
        props.currentTopPosition = this.slideHeight - this.remainingHeight

        // if groupByPeriod and there are overflowed articles render them all before visiting new model
        if (options.groupByPeriod && modelProps.modelPeriodsMap.size && props.groupByPeriodOverflowedArticles.length) {
          this.addOverflowedArticles(currentObjects, articleThumbnailObjects, headerGroup, props, modelInfoHeightProp, options, model, modelNumber, showPriceThousandsSeparated, allSlides, modelProps.modelPeriodsMap, merch, isCustomArticlePrice, promises)
          // Render Vertical divider for last iteration of overflow articles
          currentObjects.push(...this.createVerticalPeriodDividerLines(modelProps.modelPeriodsMap, props.currentTopPosition))
          const currentMaxHeight = (Math.max(this.lowestThumbnailBottomPositionInModel, (this.modelInfoHeight + modelInfoHeightProp.cwDataHeight))) + 7
          // set the remaining height for next model to start if last iteration keeps space for next model to render
          this.remainingHeight -= currentMaxHeight
        }
        else {
        // Render Vertical divider for all page except those models that have overflow article to remove duplications
          currentObjects.push(...this.createVerticalPeriodDividerLines(modelProps.modelPeriodsMap, props.currentTopPosition))
        }
        props.previousModelColumns = model.columnsRequired

        // If vr angle is included and is one model per page
        if (options.includeAllVRAngles && options.isOneModelPerPage) {
          // find the article in a model which is of lowest sort order and sort order is same then article number
          utils.sort(model.articles, ['SortOrder', 'ArticleNumber'])
          const lowestSortOrderArticle = model.articles[0]
          const images = this.findAllVRAngleImagesForModel(lowestSortOrderArticle, assetMapForVRImages)
          // draw all the vrImages
          images.forEach((image, index) => {
            this.vrImagesCount++
            const enoughSpaceInCurrentPage = this.slideHeight - (props.currentTopPosition + this.maxVRThumbnailHeight) > 0
            if ((index % this.vrImagesProp.maxImagesPerRow === 0 && index !== 0) || (index === 0 && !enoughSpaceInCurrentPage)) {
              props.currentTopPosition += this.vrImagesProp.height + this.imgVerSpacing
              const isEnoughSpaceForNextRow = this.slideHeight - (props.currentTopPosition + this.maxVRThumbnailHeight) > 0
              if (!isEnoughSpaceForNextRow) {
                if (this.isMerch) {
                  promises.push(this.createSlide(merch, props, currentObjects.slice(), options)
                    .catch((error) => {
                      // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
                      console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
                    }))
                }
                this.onNewPage(currentObjects, articleThumbnailObjects, headerGroup, props)
              }
              this.onNewRow(props, options, modelProps.modelPeriodsMap)
            }
            currentObjects.push(this.addVRImages(lowestSortOrderArticle, index, image, props))
          })
        }
      })
      // END OF MODELS LOOP

      if (this.isMerch) {
        promises.push(this.createSlide(merch, props, currentObjects.slice(), options)
          .catch((error) => {
            // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
            console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
          }))
      }
    })
    // END OF HEADER GROUPS
    return Promise.all(promises)
      .then(() => {
        this.cleanData()
        if (this.isMerch) {
          return props.slideNumber
        }
      })
  }

  generateGroups(catalog: CatalogDetails, articlesData: IArticleData, isCustomArticlePrice: boolean, sortByExcelSequence: boolean, sortByListSequence: boolean, groupByPeriod: boolean, myAttributes: Record<string, IMyAttribute>, retailPg?: CatalogPriceGroup) {
    const groups = new Map()
    const periods = new Map()
    this.includeHardLaunchDate = false
    const hardLaunchDateAttribute = catalog?.Config.HardLaunchDateAttributeSystemName
    if (utils.isDefined(hardLaunchDateAttribute) && hardLaunchDateAttribute.toString().trim() !== '') {
      this.includeHardLaunchDate = true
      this.hardLaunchDateAttribute = hardLaunchDateAttribute
    }

    this.includeUAStandardLaunchDateAttribute = false
    const UAStandardLaunchMonthAttributeValue = catalog?.Config.UAStandardLaunchMonthAttribute
    if (utils.isDefined(UAStandardLaunchMonthAttributeValue) && UAStandardLaunchMonthAttributeValue.toString().trim() !== '' && myAttributes[UAStandardLaunchMonthAttributeValue]) {
      this.includeUAStandardLaunchDateAttribute = true
      this.ShipmentStartDateAttribute = UAStandardLaunchMonthAttributeValue
      this.ShipmentStartDisplayLabel = myAttributes[UAStandardLaunchMonthAttributeValue].DisplayName
      this.infoTableStartShipWidth = this.infoTableStartShipWidth + this.infoTableEndShipWidth
      // making end ship width to 0 as we are not displaying ship end.
      this.infoTableEndShipWidth = 0
    }
    if (!this.includeUAStandardLaunchDateAttribute) {
      if (utils.isDefined(catalog.IsShippingWindowApply) && catalog.IsShippingWindowApply === 0) {
        this.ShipmentStartDateAttribute = 'ShipmentStartDate_ATT'
        this.ShipmentEndDateAttribute = 'ShipmentEndDate_ATT'
      }
    }

    // sort articles only if sort by excel sequence or list sequence option is false
    if (!sortByExcelSequence && !sortByListSequence) {
      let attributesForGrouping: string[] = ['EndUse', 'Gender', 'UADivision', 'SortOrder', 'ArticleNumber']
      attributesForGrouping = attributesForGrouping.reduce((acu, attribute) => {
        if (utils.isDefined(myAttributes[attribute])) {
          acu.push(attribute)
        }
        return acu
      }, [] as Array<string>)
      utils.sort(articlesData.articles, attributesForGrouping)
    }

    // re-adjust colorway width based on inclusion/exclusion of MarkdownDate & hardLaunchData attributes
    if (utils.isDefined(articlesData.articles[0]) && articlesData.articles[0].hasOwnProperty('MarkdownDate')) {
      this.includeMarkdownDate = true
      if (this.includeHardLaunchDate) {
        this.infoTableColorwayWidth = 160
      }
      else {
        this.infoTableColorwayWidth = 240
      }
    }
    else {
      this.includeMarkdownDate = false
      if (this.includeHardLaunchDate) {
        this.infoTableColorwayWidth = 250
      }
      else {
        this.infoTableColorwayWidth = 275
      }
    }

    // use in case of when sortByExcelSequence  or sortByListSequence is true only
    let previousSep = ''

    if (groupByPeriod) {
      articlesData.articles.forEach((article) => {
        // create map of periods
        if (utils.isDefined(article.Period) && article.Period.toString().trim() !== '' && !periods.has(article.Period.toString().toLowerCase())) {
          periods.set(article.Period.toString().toLowerCase(), { value: article.Period, articleId: article.Id })
        }
      })

      const periodsEntries = Array.from(periods)
      utils.sort(periodsEntries, ['0'])
      periodsEntries.forEach(([key, value], index) => {
        console.log(key)
        value.index = index
      })
      // get top 3 after sorting
      this.periodsMap = new Map(periodsEntries.slice(0, 3))
    }

    articlesData.articles.forEach((article, articleIndex) => {
      if (!article.ModelNumber || article.ModelNumber.trim() === '') {
        return
      }

      // skip articles that does not have value for Period or Period value is not one of the selected Period for grouping if groupByPeriod is true
      const periodLower = article.Period != null ? article.Period.toString().toLowerCase() : article.Period
      if (groupByPeriod && (periodLower == null || periodLower.toString().trim() === '' || !this.periodsMap.has(periodLower))) {
        return
      }

      const modelNumber = article.ModelNumber
      const endUse = utils.isDefined(myAttributes.EndUse) && utils.isDefined(article.EndUse) ? article.EndUse.toString().charAt(0).toUpperCase() + article.EndUse.toString().slice(1).toLowerCase() : ''
      const gender = utils.isDefined(myAttributes.Gender) && utils.isDefined(article.Gender) ? article.Gender.toString().charAt(0).toUpperCase() + article.Gender.toString().slice(1).toLowerCase() : ''
      const division = utils.isDefined(myAttributes.UADivision) && utils.isDefined(article.UADivision) ? article.UADivision.toString().charAt(0).toUpperCase() + article.UADivision.toString().slice(1).toLowerCase() : ''
      let sep = `${endUse}//${gender}//${division}`

      const model = {
        modelInfo: {
          endUse,
          gender,
          division,
          articleName: article.ArticleName,
          articleId: article.Id, // use to retrieve the persisted objects
          price: article._Prices && retailPg && article._Prices[retailPg.Id] != null ? article._Prices[retailPg.Id].Price : null,
          currency: retailPg != null ? retailPg.CurrencyCode : '',
          platform: article.Merch_Collection ? article.Merch_Collection : '',
          fabric: article.Fabric,
          sizeScale: article.SizeScale ? article.SizeScale : '',
          colorwayCode: article.ColorwayCode,
          colorDescription: article.Colorway,
          featuresAndBenefits: article.FeaturesAndBenefits != null ? article.FeaturesAndBenefits : null,
          customPrice: 0,
        },
        articles: [article],
        columnsRequired: [periodLower],
      }

      if (isCustomArticlePrice && articlesData.articlePrices) {
        if (articlesData.articlePrices && articlesData.articlePrices[article.ArticleNumber]) {
          model.modelInfo.customPrice = articlesData.articlePrices[article.ArticleNumber]
        }
        else {
          model.modelInfo.customPrice = 0
        }
      }
      // check if previous row separator is same as current row sep  if sortByExcelSequence is true
      if (((sortByExcelSequence || sortByListSequence) && previousSep.includes(sep)) || ((!sortByExcelSequence || !sortByListSequence) && groups.has(sep))) {
        if (sortByExcelSequence || sortByListSequence) {
          sep = previousSep
        }
        if (groups.get(sep).modelGroups.has(modelNumber)) {
          groups.get(sep).modelGroups.get(modelNumber).articles.push(article)
          if (!groups.get(sep).modelGroups.get(modelNumber).columnsRequired.includes(periodLower)) {
            groups.get(sep).modelGroups.get(modelNumber).columnsRequired.push(periodLower)
          }
        }
        else {
          groups.get(sep).modelGroups.set(modelNumber, model)
        }
      }
      else {
        if (sortByExcelSequence || sortByListSequence) {
          // add suffix to avoid adding articles that belong to this group but positioned in excel such a way that they should be visible after set of different header groups
          sep += articleIndex
        }
        const headerGroup = {
          headerInfo: {
            articleId: article.Id,
            title: {
              articleProperties: ['EndUse'],
              value: `${endUse}`,
            },
            subtitle: {
              articleProperties: ['Gender', 'UADivision'],
              value: `${gender} - ${division}  `,
            },
          },
          modelGroups: new Map(),
        }
        headerGroup.modelGroups.set(modelNumber, model)
        groups.set(sep, headerGroup)
      }
      // use in case of when sortByExcelSequence or sortByListSequence is true only
      previousSep = sep
    })

    return groups
  }

  // estimate the height for colorway table content
  calculateColorwayTableContentHeightForOneRow(articleLength: number) {
    if (articleLength > this.maxNumberOfImagePerRow) {
      return this.labelHeightPerLine * this.maxNumberOfImagePerRow
    }
    else {
      return this.labelHeightPerLine * articleLength
    }
  }

  // get maximum height a thumbnail would need with 4 line of thumbnail details
  getMaxThumbnailHeight() {
    return this.imgSize + this.imgVerSpacing + 4 * this.labelHeightPerLine
  }

  // get maximum height a VR thumbnail image would need
  getMaxVRThumbnailHeight() {
    return this.vrImagesProp.height + this.imgVerSpacing
  }

  isTwoArraysSame(array1: any[], array2: any[]) {
    // if length is not equal
    if (array1.length !== array2.length) { return false }
    else {
      let valid = true
      // comparing each element of array
      for (let i = 0; i < array1.length; i++) {
        if (!array2.includes(array1[i])) {
          valid = false
        }
      }
      return valid
    }
  }

  findAllVRAngleImagesForModel(article: MyArticle, assetMapForVRImages: Record<string, DuneAsset[]>) {
    let vrImageTypes: DuneAsset[] = []
    if (assetMapForVRImages && assetMapForVRImages[article.ArticleNumber]) {
      // Added the regex to start the image name with vr
      const pattern = /^vr.*/
      vrImageTypes = assetMapForVRImages[article.ArticleNumber].filter(image => pattern.test(image.Key.toString().toLowerCase()))
    }
    return vrImageTypes
  }

  createSlide(merch, props: IProp, objects: any[], options: Record<string, any>) {
    props.slideNumber++
    let slideName: string = ''
    if (options.isOneModelPerPage) {
      slideName = props.articleName
    }
    else {
      slideName = `slide - ${padStart(props.slideNumber.toString(), 3, '0')}`
    }
    const folderNode = options.folder
    const sortOrder = Math.max(...folderNode.children.map(child => child.sortOrder), 0) + 1
    const folderStructure = findParentPath(folderNode)
    if (utils.isDefined(folderNode.children.find(node => node.label === slideName))) {
      let c = 0
      do {
        c++
        slideName = `${slideName} (${c})`
      } while (folderNode.children.find(node => node.label === slideName) !== undefined)
    }
    return merch!.addSlide(folderStructure.id, folderStructure.name, folderNode.sortOrder, slideName, objects, options.slideSize, merchConstants.slideImageDefaultScaleFactors.large, sortOrder, true, props.articleIdList)
  }

  addVRImages(article: MyArticle, index: number, image: DuneAsset, props: IProp) {
    const imageInRowIndex = index % this.vrImagesProp.maxImagesPerRow
    const top = props.currentTopPosition + this.imgVerSpacing + this.vrImagesProp.width / 2
    const left = imageInRowIndex * (this.vrImagesProp.width + this.imgHorSpacing) + this.vrImagesProp.width / 2 + this.imgHorSpacing
    const object = this.createImage(top, left, this.vrImagesProp.height, this.vrImagesProp.width, article.Id, image.Key, 0.6, 0.6)
    if (index % this.vrImagesProp.maxImagesPerRow === 0 || index === 0) {
      this.lowestThumbnailBottomPositionInModel += this.vrImagesProp.height + this.imgVerSpacing
    }
    if (!props.articleIdList.includes(article.Id)) {
      props.articleIdList.push(article.Id)
    }
    return object
  }

  addOverflowedArticles(currentObjects: any[], articleThumbnailObjects: any[], headerGroup, props: IProp, modelInfoHeightProp, options: Record<string, any>, model, modelNumber: number, showPriceThousandsSeparated, allSlides, periodsMap, merch, isCustomArticlePrice, promises) {
    // create the page by saving the canvas or creating objects
    currentObjects.push(...this.createVerticalPeriodDividerLines(periodsMap, props.currentTopPosition))
    // create the page by saving the canvas or creating objects
    if (this.isMerch) {
      promises.push(this.createSlide(merch, props, currentObjects.slice(), options)
        .catch((error) => {
          // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
          console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
        }))
    }
    const articles = clone(props.groupByPeriodOverflowedArticles)
    // NEW PAGE
    modelInfoHeightProp.cwDataHeight = 0
    this.onNewPage(currentObjects, articleThumbnailObjects, headerGroup, props)
    // if group by period
    if (options.groupByPeriod && periodsMap.size > 0) {
      this.periodOnNewPage(periodsMap, props, options, currentObjects)
    }
    this.onNewRow(props, options, periodsMap)
    // add model Info and table header
    currentObjects.push(...this.addModelInfoAndTableHeader(model, props, periodsMap, options, modelNumber, isCustomArticlePrice, false, showPriceThousandsSeparated))
    articles.forEach((article, index) => {
      currentObjects.push(...this.addArticle(article, index, options, props, modelInfoHeightProp, periodsMap))
    })
    props.currentTopPosition = this.lowestThumbnailBottomPositionInModel
    if (props.groupByPeriodOverflowedArticles.length) {
      this.addOverflowedArticles(currentObjects, articleThumbnailObjects, headerGroup, props, modelInfoHeightProp, options, model, modelNumber, showPriceThousandsSeparated, allSlides, periodsMap, merch, isCustomArticlePrice, promises)
    }
    return currentObjects
  }

  addArticle(article: MyArticle, articlesArrayIndex: number, options: Record<string, any>, props: IProp, modelInfoHeightProp, periodsMap) {
    const mapper = { 0: 'c1', 1: 'c2', 2: 'c3' }
    const objects: any[] = []
    let updateLowestThumbnailBottomPosition = false

    const imageType = options.imageType
    let imageInRowIndex = articlesArrayIndex % props.maxImagePerRow
    let currentTopPosition = props.currentTopPosition
    let periodCurrentColumnsInfo
    let periodCurrentColumnTracker
    let groupByPeriodCurrentGroupColumnIndex = 0

    // if group by period is true
    if (options.groupByPeriod && periodsMap.size) {
      const articlePeriodLower = article.Period != null ? article.Period.toString().toLowerCase() : article.Period
      const period = periodsMap.has(articlePeriodLower) ? periodsMap.get(articlePeriodLower) : null
      if (!period) { // if article period is not in selected top 3 skip the article
        return objects
      }

      periodCurrentColumnsInfo = this.periodColumnsInfo[mapper[period.index]]
      periodCurrentColumnTracker = props.groupByPeriodColumnTracker[period.index]

      if (periodCurrentColumnTracker.pageFull) {
        props.groupByPeriodOverflowedArticles.push(article)
        return objects
      }

      currentTopPosition = periodCurrentColumnTracker.currentTopPosition
      groupByPeriodCurrentGroupColumnIndex = periodCurrentColumnTracker.currentColumn

      // update periodCurrentColumnTracker
      periodCurrentColumnTracker.currentColumn = periodCurrentColumnTracker.currentColumn + 1
      if (periodCurrentColumnTracker.currentColumn > periodCurrentColumnsInfo.maxThumbnailInARow) {
        // go to next row, reset currentColumn and update currentTopPosition
        updateLowestThumbnailBottomPosition = true
        periodCurrentColumnTracker.currentColumn = 1
        periodCurrentColumnTracker.currentTopPosition += this.imgSize + this.imgVerSpacing + props.currentRowMaxLabelHeight
        currentTopPosition = periodCurrentColumnTracker.currentTopPosition

        const isEnoughSpaceForNextRow = this.slideHeight - (currentTopPosition + this.maxThumbnailHeight) > 0
        // if not enough space for another row of data add it to overflowedArticles array
        if (!isEnoughSpaceForNextRow) {
          props.groupByPeriodOverflowedArticles.push(article)
          periodCurrentColumnTracker.pageFull = true
          return objects
        }
      }

      // find imageInRowIndex
      imageInRowIndex = periodCurrentColumnsInfo.maxTrailingThumbnailsFromPreviousColumns + periodCurrentColumnTracker.currentColumn - 1
    }
    const top = currentTopPosition + this.imgVerSpacing + this.imgSize / 2
    const left = this.modelInfoWidth + (imageInRowIndex * (this.imgSize + this.imgHorSpacing) + this.imgSize / 2 + this.imgHorSpacing)
    objects.push(this.createImage(top, left, 500, 500, article.Id, imageType, this.imgSize / 500, this.imgSize / 500))
    if (!props.articleIdList.includes(article.Id)) {
      props.articleIdList.push(article.Id)
    }

    const thumbnailInfoHeight = this.labelHeightPerLine * this.thumbnailProps.length
    const thumbnailInfoWidth = this.imgSize
    const thumbnailInfoTop = currentTopPosition + this.imgSize + this.imgVerSpacing
    const thumbnailInfoLeft = this.modelInfoWidth + (imageInRowIndex * (this.imgSize + this.imgHorSpacing) + this.imgHorSpacing)
    const articleThumbnail = this.createLabel(article.Id, this.thumbnailProps, '\n', thumbnailInfoTop, thumbnailInfoLeft, thumbnailInfoWidth, thumbnailInfoHeight, 10, 'normal', 'center', '#757575', '#FFFFFF', false, false)
    objects.push(articleThumbnail)

    objects.push(...this.createInfoTableText(article, props))
    modelInfoHeightProp.cwDataHeight += this.labelHeightPerLine

    // if group by period is true
    if (options.groupByPeriod && periodsMap.size) {
      if (updateLowestThumbnailBottomPosition || articlesArrayIndex === 0 || groupByPeriodCurrentGroupColumnIndex === 0) {
        periodCurrentColumnTracker.lowestThumbnailBottomPositionInModel += this.imgSize + this.imgVerSpacing + props.currentRowMaxLabelHeight + 20
      }
      const lowestThumbnailBottomPositionInModelValues: number[] = []
      const groupPeriods: IGroupByPeriodColumnTracker[] = Object.values(props.groupByPeriodColumnTracker)
      groupPeriods.forEach((groupPeriod) => {
        lowestThumbnailBottomPositionInModelValues.push(groupPeriod.lowestThumbnailBottomPositionInModel)
      })
      this.lowestThumbnailBottomPositionInModel = Math.max(...lowestThumbnailBottomPositionInModelValues)
    }
    else {
      if (articlesArrayIndex % props.maxImagePerRow === 0 || articlesArrayIndex === 0) {
        this.lowestThumbnailBottomPositionInModel += this.imgSize + this.imgVerSpacing + props.currentRowMaxLabelHeight + 20
      }
    }
    return objects
  }

  // free memory by cleaning instance data
  cleanData() {
    this.periodsMap = new Map()
  }

  // on new page will create page header, rest currentTopPosition, reset current row and increase page number
  onNewPage(currentObjects: any[], articleThumbnailObjects: any[], headerGroup, props: IProp) {
    this.remainingHeight = this.slideHeight - this.headerHeight

    props.currentTopPosition = this.headerHeight
    props.articleIdList = []
    currentObjects.length = 0
    articleThumbnailObjects.length = 0
    this.lowestThumbnailBottomPositionInModel = 0

    props.groupByPeriodOverflowedArticles = []

    currentObjects.push(
      ...this.createPageHeaderObjects(
        headerGroup.headerInfo.articleId,
        headerGroup.headerInfo.title,
        headerGroup.headerInfo.subtitle,
      ),
    )
  }

  periodOnNewPage(modelPeriodsMap, props, options, currentObjects) {
    props.groupByPeriodColumnTracker = {}
    Array.from(modelPeriodsMap).forEach((entry, index) => {
      props.groupByPeriodColumnTracker[index] = {
        currentColumn: 0,
        currentTopPosition: this.headerHeight,
        lowestThumbnailBottomPositionInModel: 0, // same usage as articleHeight
        pageFull: false,
      }
    })
    // if group by period
    if (options.groupByPeriod && modelPeriodsMap.size > 0) {
      currentObjects.push(...this.createPeriodColumnsAndDividers(props, options, modelPeriodsMap))
    }
  }

  /**
   * @description resets currentColumn in groupByPeriodColumnTracker
   * @param {object} props
   * @param {object} options
   */
  onNewRow(props, options, modelPeriodsMap) {
    // if group by period
    if (options.groupByPeriod && modelPeriodsMap.size) {
      Array.from(modelPeriodsMap).forEach((entry, index) => {
        props.groupByPeriodColumnTracker[index].currentColumn = 0
      })
    }
  }

  createImage(top, left, height, width, articleId, imageType, scaleX = 1, scaleY = 1) {
    return {
      type: 'articleImage',
      top,
      left,
      width,
      height,
      displayWidth: width,
      displayHeight: height,
      articleId,
      angle: 0,
      scaleX,
      scaleY,
      flipX: false,
      flipY: false,
      locked: false,
      shouldAllowTemplateBigImagesToResize: true,
      imageType,
    }
  }

  createLabel(articleId, attributes, articlePropsSeparator, top, left, width, height, fontSize, fontWeight, textAlign, color, backgroundColor, locked = false, templateObject = false) {
    if (!Array.isArray(attributes)) {
      attributes = [attributes]
    }
    if (!attributes || attributes.length === 0) { return }

    return {
      type: 'articleDetails',
      top,
      left,
      width,
      height,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      backgroundColor,
      fill: color,
      flipX: false,
      flipY: false,
      customOptions: {
        articleProps: attributes.slice(),
        articleBLCoords: { x: 0, y: 0 },
        separator: articlePropsSeparator,
      },
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign,
      locked,
      articleId,
      templateObject,
      isOriginTopLeft: true,
    }
  }

  modelInfoAndTableHeaderHeight(model, modelInfoHeightProp, isCustomArticlePrice, showPriceThousandsSeparated) {
    let totalHeight = 0
    const modelNumberTextObjectHeight = Math.ceil(this.modelNumberProp.width / this.modelInfoWidth) * this.labelHeightPerLine
    this.modelNumberProp.height = modelNumberTextObjectHeight
    // TODO: @aanchal: why does this.platformProp should occupy equal height to this.modelNumberProp height?
    this.platformProp.height = modelNumberTextObjectHeight
    // TODO: @aanchal: and why totalHeight is 1 times of modelNumberTextObjectHeight since there are 2 objects assigned to modelNumberTextObjectHeight (modelNumber and platform), is it suppose to be next to model number?
    totalHeight += modelNumberTextObjectHeight
    const articleNameTextObjectHeight = Math.ceil(this.articleNameProp.width / this.modelInfoWidth) * this.labelHeightPerLine
    totalHeight += articleNameTextObjectHeight
    this.articleNameProp.height = articleNameTextObjectHeight
    let currencyAndPriceTextWidth = 0
    let priceValue = ''
    if (isCustomArticlePrice && model.modelInfo.hasOwnProperty('customPrice')) {
      priceValue = !showPriceThousandsSeparated ? model.modelInfo.customPrice : utils.thousandsSeparator(model.modelInfo.customPrice)
    }
    else {
      priceValue = `${model.modelInfo.currency} ${!showPriceThousandsSeparated ? model.modelInfo.price : utils.thousandsSeparator(model.modelInfo.price)} `
    }
    currencyAndPriceTextWidth = utils.getTextWidth(priceValue, `${this.priceAndFabricProp.fontWeight} ${this.priceAndFabricProp.fontSize}px ${this.font}`)
    this.priceAndFabricProp.priceWidth = currencyAndPriceTextWidth
    const doubleSlashText = ' // '
    const doubleSlashTextObjectWidth = utils.getTextWidth(
      doubleSlashText,
      `${this.priceAndFabricProp.fontWeight} ${this.priceAndFabricProp.fontSize}px ${this.font}`,
    )
    // TODO: @aanchal don't we need to consider width of sizeScale when assigning width for fabric?
    const fabricTextObjectWidth = this.modelInfoWidth - (currencyAndPriceTextWidth + doubleSlashTextObjectWidth + 5)
    // TODO: @aanchal what is hardcoded - 10? and why are you doing Math.ceil(fabricTextObjectWidth / this.modelInfoWidth) when you are not considering it to be in multi line?
    const fabricTextObjectHeight = Math.ceil(fabricTextObjectWidth / this.modelInfoWidth) * this.labelHeightPerLine - 10
    totalHeight += fabricTextObjectHeight
    this.priceAndFabricProp.height = fabricTextObjectHeight

    if (model.modelInfo.featuresAndBenefits != null && model.modelInfo.featuresAndBenefits.toString().trim() !== '') {
      let perFeatureTextForHeight = ''
      let i = 0
      const featuresAndBenefitArray = model.modelInfo.featuresAndBenefits.split(/\n/g)
      featuresAndBenefitArray.forEach((feature) => {
        i++
        const textWidth = utils.getTextWidth(feature, `${this.featureAndBenefitsProp.fontWeight} ${this.featureAndBenefitsProp.fontSize}px ${this.font}`)
        if (textWidth > this.featureAndBenefitsProp.width - 5) {
          const perFeatureNumberOfLines = Math.ceil(textWidth / (this.featureAndBenefitsProp.width - 5))
          for (let j = 0; j < perFeatureNumberOfLines; j++) {
            perFeatureTextForHeight += feature
            perFeatureTextForHeight += '\n'
          }
        }
        // TODO: @aanchal shouldn't the following line place under else block?
        perFeatureTextForHeight += feature
        if (featuresAndBenefitArray.length !== i) { perFeatureTextForHeight += '\n' }
      })
      this.featureAndBenefitsProp.height = utils.getTextHeight(perFeatureTextForHeight, this.featureAndBenefitsProp.fontSize, this.font, fabric)
      totalHeight += this.featureAndBenefitsProp.height + this.featureAndBenefitsProp.marginTop + this.featureAndBenefitsProp.marginBottom
    }
    totalHeight += modelInfoHeightProp.margin + modelInfoHeightProp.cwAndColorwayHeader
    return totalHeight
  }

  addModelInfoAndTableHeader(model, props, modelPeriodsMap, options, modelNumber, isCustomArticlePrice, addNewLine, showPriceThousandsSeparated) {
    const leftMargin = 5
    props.highlight = 1
    let currentTopPosition = 0
    if (options.groupByPeriod && modelPeriodsMap.size) {
      Array.from(modelPeriodsMap).forEach((entry, index) => {
        props.groupByPeriodColumnTracker[index].currentTopPosition += 10
        currentTopPosition = props.groupByPeriodColumnTracker[index].currentTopPosition
      })
    }
    else {
      props.currentTopPosition += 10
      currentTopPosition = props.currentTopPosition
    }
    const modelInfoAndTableHeaderObjects: any[] = []
    const modelNumberTextObjectTop = currentTopPosition + 2
    if (addNewLine) {
      modelInfoAndTableHeaderObjects.push(this.addLine(currentTopPosition, 5 + (this.modelInfoWidth - 20) / 2, 0, this.modelInfoWidth - 20))

      modelInfoAndTableHeaderObjects.push(this.addLine(currentTopPosition, (this.modelInfoWidth + ((this.slideWidth - this.modelInfoWidth) / 2)), 0, this.slideWidth - this.modelInfoWidth - 10))
      //
      // platform
      // TODO: @Aanchal once you see the comment please explain me why the left for platform decided to be 15
      const platformTextObject = this.createTextObject(
        model.modelInfo.platform,
        modelNumberTextObjectTop,
        15,
        this.platformProp.width,
        this.platformProp.height,
        this.platformProp.fontSize,
        this.platformProp.fontWeight,
        'left',
        '#FF0000',
        false,
      )

      modelInfoAndTableHeaderObjects.push(platformTextObject)
    }
    // model number
    const modelNumberTextObject = this.createLabel(
      model.modelInfo.articleId,
      'ModelNumber',
      '\n',
      modelNumberTextObjectTop,
      leftMargin,
      this.modelNumberProp.width,
      this.modelNumberProp.height,
      this.modelNumberProp.fontSize,
      this.modelNumberProp.fontWeight,
      'left',
      '#757575',
      '#FFFFFF',
      false,
    )
    modelInfoAndTableHeaderObjects.push(modelNumberTextObject)
    // article name
    const articleNameTextObjectTop
      = modelNumberTextObjectTop + this.modelNumberProp.height
    const articleNameTextObject = this.createLabel(
      model.modelInfo.articleId,
      'ArticleName',
      '\n',
      articleNameTextObjectTop,
      leftMargin,
      this.articleNameProp.width,
      this.articleNameProp.height,
      this.articleNameProp.fontSize,
      this.articleNameProp.fontWeight,
      'left',
      '#000000',
      '#FFFFFF',
      false,
    )
    modelInfoAndTableHeaderObjects.push(articleNameTextObject)

    // currency price and fabric

    // custom price
    const priceAndFabricTextObjectTop
      = articleNameTextObjectTop + this.articleNameProp.height
    const currencyAndPriceTextObjectHeight = this.labelHeightPerLine
    if (isCustomArticlePrice && model.modelInfo.hasOwnProperty('customPrice')) {
      const priceAndCurrencyTextObject = this.createTextObject(
        !showPriceThousandsSeparated ? model.modelInfo.customPrice : utils.thousandsSeparator(model.modelInfo.customPrice),
        priceAndFabricTextObjectTop,
        leftMargin,
        this.priceAndFabricProp.priceWidth,
        currencyAndPriceTextObjectHeight,
        this.priceAndFabricProp.fontSize,
        this.priceAndFabricProp.fontWeight,
        'left',
        '#757575',
        false,
      )
      modelInfoAndTableHeaderObjects.push(priceAndCurrencyTextObject)
    }
    else {
      // Retail price
      const priceTextObject = this.createLabel(
        model.modelInfo.articleId,
        '_RetailPrice',
        '',
        priceAndFabricTextObjectTop,
        leftMargin,
        this.priceAndFabricProp.priceWidth,
        currencyAndPriceTextObjectHeight,
        this.priceAndFabricProp.fontSize,
        this.priceAndFabricProp.fontWeight,
        'left',
        '#757575',
        '#FFFFFF',
        false,
      )
      modelInfoAndTableHeaderObjects.push(priceTextObject)
    }

    // double slash
    const doubleSlashText = ' // '
    const doubleSlashTextObjectWidth = utils.getTextWidth(
      doubleSlashText,
      `${this.priceAndFabricProp.fontWeight} ${this.priceAndFabricProp.fontSize}px ${this.font}`,
    )
    const doubleSlashTextObjectLeft = leftMargin + this.priceAndFabricProp.priceWidth
    const doubleSlashTextObject = this.createTextObject(
      doubleSlashText,
      priceAndFabricTextObjectTop,
      doubleSlashTextObjectLeft,
      doubleSlashTextObjectWidth,
      currencyAndPriceTextObjectHeight,
      this.priceAndFabricProp.fontSize,
      this.priceAndFabricProp.fontWeight,
      'left',
      '#757575',
      false,
    )
    modelInfoAndTableHeaderObjects.push(doubleSlashTextObject)

    // size scale
    const sizeScaleTextObjectWidth = utils.getTextWidth(
      model.modelInfo.sizeScale,
      `${this.priceAndFabricProp.fontWeight} ${this.priceAndFabricProp.fontSize}px ${this.font}`,
    )
    const sizeScaleLeft = doubleSlashTextObjectLeft + doubleSlashTextObjectWidth
    const sizeScaleLeftTextObject = this.createLabel(
      model.modelInfo.articleId,
      'SizeScale',
      '',
      priceAndFabricTextObjectTop,
      sizeScaleLeft,
      sizeScaleTextObjectWidth,
      currencyAndPriceTextObjectHeight,
      this.priceAndFabricProp.fontSize,
      this.priceAndFabricProp.fontWeight,
      'left',
      '#757575',
      '#FFFFFF',
      false,
    )
    modelInfoAndTableHeaderObjects.push(sizeScaleLeftTextObject)

    // double slash
    const doubleSlashSecondLeft = sizeScaleLeft + sizeScaleTextObjectWidth
    const doubleSlashTextObjectSecond = this.createTextObject(
      doubleSlashText,
      priceAndFabricTextObjectTop,
      doubleSlashSecondLeft,
      doubleSlashTextObjectWidth,
      currencyAndPriceTextObjectHeight,
      this.priceAndFabricProp.fontSize,
      this.priceAndFabricProp.fontWeight,
      'left',
      '#757575',
      false,
    )
    modelInfoAndTableHeaderObjects.push(doubleSlashTextObjectSecond)
    // fabric
    // assign rest of width
    const fabricTextObjectWidth = this.modelInfoWidth - (doubleSlashSecondLeft + doubleSlashTextObjectWidth)

    const fabricTextObjectHeight = Math.ceil(fabricTextObjectWidth / this.modelInfoWidth) * this.labelHeightPerLine
    const fabricTextObjectLeft = doubleSlashSecondLeft + doubleSlashTextObjectWidth
    const fabricTextObject = this.createLabel(
      model.modelInfo.articleId,
      'Fabric',
      '\n',
      priceAndFabricTextObjectTop,
      fabricTextObjectLeft,
      fabricTextObjectWidth,
      fabricTextObjectHeight - 10,
      this.priceAndFabricProp.fontSize,
      this.priceAndFabricProp.fontWeight,
      'left',
      '#757575',
      '#FFFFFF',
      false,
    )
    modelInfoAndTableHeaderObjects.push(fabricTextObject)

    // features and benefits
    let featuresTextObjectTop = priceAndFabricTextObjectTop
    let featuresTextObjectHeight = fabricTextObjectHeight
    if (model.modelInfo.featuresAndBenefits != null && model.modelInfo.featuresAndBenefits.toString().trim() !== '') {
      featuresTextObjectHeight = this.featureAndBenefitsProp.height
      featuresTextObjectTop = priceAndFabricTextObjectTop + fabricTextObjectHeight + this.featureAndBenefitsProp.marginTop
      const featuresTextObject = this.createLabel(
        model.modelInfo.articleId,
        'FeaturesAndBenefits',
        '\n',
        featuresTextObjectTop,
        leftMargin,
        this.featureAndBenefitsProp.width,
        featuresTextObjectHeight,
        this.featureAndBenefitsProp.fontSize,
        this.featureAndBenefitsProp.fontWeight,
        'left',
        '#757575',
        '#FFFFFF',
        false,
      )
      modelInfoAndTableHeaderObjects.push(featuresTextObject)
    }
    // table header
    // CW#
    const tableHeaderFontSize = 10
    const tableHeaderFontWeight = 'bold'
    const tableHeaderTop = 5 + featuresTextObjectTop + featuresTextObjectHeight + this.featureAndBenefitsProp.marginBottom

    const CWText = 'CW#'
    const CWTextObjectWidth = this.infoTableColorwayCodeWidth
    const tableHeaderHeight = this.labelHeightPerLine
    const CWTextObjectLeft = leftMargin
    const CWTextObject = this.createTextObject(
      CWText,
      tableHeaderTop,
      CWTextObjectLeft,
      CWTextObjectWidth,
      tableHeaderHeight,
      tableHeaderFontSize,
      tableHeaderFontWeight,
      'left',
      '#8B8B8B',
      false,
    )
    modelInfoAndTableHeaderObjects.push(CWTextObject)

    // Colorway number
    const ColorwayNumberText = 'Colorway Name'
    const ColorwayNumberTextObjectWidth = this.infoTableColorwayWidth
    const ColorwayNumberTextObjectLeft = CWTextObjectLeft + CWTextObjectWidth
    const ColorwayNumberTextObject = this.createTextObject(
      ColorwayNumberText,
      tableHeaderTop,
      ColorwayNumberTextObjectLeft,
      ColorwayNumberTextObjectWidth,
      tableHeaderHeight,
      tableHeaderFontSize,
      tableHeaderFontWeight,
      'left',
      '#8B8B8B',
      false,
    )
    modelInfoAndTableHeaderObjects.push(ColorwayNumberTextObject)

    // start ship
    const startShipText = this.ShipmentStartDisplayLabel
    const startShipTextObjectWidth = this.infoTableStartShipWidth
    const startShipTextObjectLeft = ColorwayNumberTextObjectLeft + ColorwayNumberTextObjectWidth
    const startShipTextObject = this.createTextObject(
      startShipText,
      tableHeaderTop,
      startShipTextObjectLeft,
      startShipTextObjectWidth,
      tableHeaderHeight,
      tableHeaderFontSize,
      tableHeaderFontWeight,
      'left',
      '#8B8B8B',
      false,
    )
    modelInfoAndTableHeaderObjects.push(startShipTextObject)

    // hard launch date
    let endShipTextObjectLeft = 0
    if (this.includeHardLaunchDate) {
      const hardLaunchDateText = 'H. Launch Date'
      const hardLaunchDateTextObjectWidth = this.infoTableHardLaunchDateWidth
      const hardLaunchDateTextObjectLeft = startShipTextObjectLeft + startShipTextObjectWidth
      const hardLaunchDateTextObject = this.createTextObject(
        hardLaunchDateText,
        tableHeaderTop,
        hardLaunchDateTextObjectLeft,
        hardLaunchDateTextObjectWidth,
        tableHeaderHeight,
        tableHeaderFontSize,
        tableHeaderFontWeight,
        'left',
        '#8B8B8B',
        false,
      )
      modelInfoAndTableHeaderObjects.push(hardLaunchDateTextObject)
      endShipTextObjectLeft = hardLaunchDateTextObjectLeft + hardLaunchDateTextObjectWidth
    }
    else {
      endShipTextObjectLeft = startShipTextObjectLeft + startShipTextObjectWidth
    }
    const endShipTextObjectWidth = this.infoTableEndShipWidth
    if (!this.includeUAStandardLaunchDateAttribute) {
      // end ship
      const endShipText = 'Ship End'
      const endShipTextObject = this.createTextObject(
        endShipText,
        tableHeaderTop,
        endShipTextObjectLeft,
        endShipTextObjectWidth,
        tableHeaderHeight,
        tableHeaderFontSize,
        tableHeaderFontWeight,
        'left',
        '#8B8B8B',
        false,
      )
      modelInfoAndTableHeaderObjects.push(endShipTextObject)
    }

    if (this.includeMarkdownDate) {
      // Markdown Date
      const markdownText = 'Markdown Date'
      const markdownTextObjectWidth = this.infoTableMarkdownDateWidth
      const markdownTextObjectLeft = endShipTextObjectLeft + endShipTextObjectWidth
      const markdownTextObject = this.createTextObject(
        markdownText,
        tableHeaderTop,
        markdownTextObjectLeft,
        markdownTextObjectWidth,
        tableHeaderHeight,
        tableHeaderFontSize,
        tableHeaderFontWeight,
        'left',
        '#8B8B8B',
        false,
      )
      modelInfoAndTableHeaderObjects.push(markdownTextObject)
    }

    props.infoTableTopPosition = tableHeaderTop + 15
    return modelInfoAndTableHeaderObjects
  }

  createPeriodColumnsAndDividers(props, options, modelPeriodsMap) {
    let currentTopPosition = 0
    if (options.groupByPeriod && modelPeriodsMap.size) {
      Array.from(modelPeriodsMap).forEach((entry, index) => {
        props.groupByPeriodColumnTracker[index].currentTopPosition += 10
        currentTopPosition = props.groupByPeriodColumnTracker[index].currentTopPosition
      })
      this.remainingHeight -= 10
    }
    else {
      props.currentTopPosition += 10
      currentTopPosition = props.currentTopPosition
    }
    const objects: any[] = []
    if (modelPeriodsMap.size === 1) {
      // max images
      this.periodColumnsInfo.c1.maxThumbnailInARow = this.maxNumberOfImagePerRow
    }
    else if (modelPeriodsMap.size === 2) {
      // 4 images
      this.periodColumnsInfo.c1.maxThumbnailInARow = 4
      this.periodColumnsInfo.c2.maxTrailingThumbnailsFromPreviousColumns = 4
    }
    else if (modelPeriodsMap.size === 3) {
      // 3 images
      this.periodColumnsInfo.c1.maxThumbnailInARow = 3
      this.periodColumnsInfo.c2.maxTrailingThumbnailsFromPreviousColumns = 3
    }
    this.periodColumnsInfo.c1.width = this.periodColumnsInfo.c1.maxThumbnailInARow * (this.imgSize + this.imgHorSpacing)

    // calculate height for each column
    const periodsEntries: any[] = Array.from(modelPeriodsMap)
    const c1Text = periodsEntries.length > 0 ? periodsEntries[0][1].value : ''
    const c2Text = periodsEntries.length > 1 ? periodsEntries[1][1].value : ''
    const c3Text = periodsEntries.length > 2 ? periodsEntries[2][1].value : ''
    let c1TextWidth = 0
    let c2TextWidth = 0
    let c3TextWidth = 0

    if (c1Text != null && c1Text.toString().trim() !== '') {
      c1TextWidth = utils.getTextWidth(c1Text, `${this.periodsHeaderProp.fontWeight} ${this.periodsHeaderProp.fontSize}px ${this.periodsHeaderProp.font}`)
      this.periodColumnsInfo.c1.height = this.getTextRequiredHeightBasedOnWidth(c1Text, this.periodColumnsInfo.c1.width, this.periodsHeaderProp.font, this.periodsHeaderProp.fontSize, this.periodsHeaderProp.fontWeight)
    }
    if (c2Text != null && c2Text.toString().trim() !== '') {
      c2TextWidth = utils.getTextWidth(c2Text, `${this.periodsHeaderProp.fontWeight} ${this.periodsHeaderProp.fontSize}px ${this.periodsHeaderProp.font}`)
      this.periodColumnsInfo.c2.height = this.getTextRequiredHeightBasedOnWidth(c2Text, this.periodColumnsInfo.c2.width, this.periodsHeaderProp.font, this.periodsHeaderProp.fontSize, this.periodsHeaderProp.fontWeight)
    }
    if (c3Text != null && c3Text.toString().trim() !== '') {
      c3TextWidth = utils.getTextWidth(c3Text, `${this.periodsHeaderProp.fontWeight} ${this.periodsHeaderProp.fontSize}px ${this.periodsHeaderProp.font}`)
      this.periodColumnsInfo.c3.height = this.getTextRequiredHeightBasedOnWidth(c3Text, this.periodColumnsInfo.c3.width, this.periodsHeaderProp.font, this.periodsHeaderProp.fontSize, this.periodsHeaderProp.fontWeight)
    }
    const headerWidth = Math.max(c1TextWidth, c2TextWidth, c3TextWidth) + 20
    const headerHeight = Math.max(this.periodColumnsInfo.c1.height, this.periodColumnsInfo.c2.height, this.periodColumnsInfo.c3.height)

    // column 1
    if (c1Text != null && c1Text.toString().trim() !== '') {
      // const boxTop = currentTopPosition
      const boxLeft = this.modelInfoWidth + this.periodColumnsInfo.c1.width / 2
      const labelLeft = boxLeft - (headerWidth / 2) + 5
      const labelTop = currentTopPosition - (this.periodColumnsInfo.c1.height / 2) + 5
      // objects.push(this.createRectangle(boxTop, boxLeft, headerWidth, headerHeight, '#aaaaaa', '#aaaaaa'))
      objects.push(this.createLabel(periodsEntries[0][1].articleId, 'Period', '\n', labelTop, labelLeft, headerWidth, headerHeight, this.periodsHeaderProp.fontSize, this.periodsHeaderProp.fontWeight, 'center', '#000000', '#aaaaaa', true))
    }

    // column 2
    if (c2Text != null && c2Text.toString().trim() !== '') {
      const boxTop = currentTopPosition
      const boxLeft = this.modelInfoWidth + this.periodColumnsInfo.c1.width + this.periodColumnsInfo.c2.width / 2
      const labelLeft = boxLeft - (headerWidth / 2) + 5
      const labelTop = currentTopPosition - (this.periodColumnsInfo.c2.height / 2) + 5
      const verticalDividerHeight = this.slideHeight - (boxTop + this.labelHeightPerLine)
      const verticalDividerTop = (verticalDividerHeight / 2) + (boxTop + this.labelHeightPerLine / 2)
      const verticalDividerLeft = this.modelInfoWidth + this.periodColumnsInfo.c1.width + this.imgHorSpacing / 2
      this.periodColumnsInfo.c2.dividerTop = verticalDividerTop
      this.periodColumnsInfo.c2.dividerLeft = verticalDividerLeft
      this.periodColumnsInfo.c2.dividerHeight = verticalDividerHeight

      // objects.push(this.createRectangle(boxTop, boxLeft, headerWidth, headerHeight, '#aaaaaa', '#aaaaaa'))
      objects.push(this.createLabel(periodsEntries[1][1].articleId, 'Period', '\n', labelTop, labelLeft, headerWidth, headerHeight, this.periodsHeaderProp.fontSize, this.periodsHeaderProp.fontWeight, 'center', '#000000', '#aaaaaa', true))
    }

    // column 3
    if (c3Text != null && c3Text.toString().trim() !== '') {
      const boxTop = currentTopPosition
      const boxLeft = this.modelInfoWidth + this.periodColumnsInfo.c1.width + this.periodColumnsInfo.c2.width + this.periodColumnsInfo.c3.width / 2
      const labelLeft = boxLeft - (headerWidth / 2) + 5
      const labelTop = currentTopPosition - (this.periodColumnsInfo.c3.height / 2) + 5
      const verticalDividerHeight = this.slideHeight - (boxTop + this.labelHeightPerLine)
      const verticalDividerTop = (verticalDividerHeight / 2) + (boxTop + this.labelHeightPerLine / 2)
      const verticalDividerLeft = this.modelInfoWidth + this.periodColumnsInfo.c1.width + this.periodColumnsInfo.c2.width + this.imgHorSpacing / 2
      this.periodColumnsInfo.c3.dividerTop = verticalDividerTop
      this.periodColumnsInfo.c3.dividerLeft = verticalDividerLeft
      this.periodColumnsInfo.c3.dividerHeight = verticalDividerHeight
      // objects.push(this.createRectangle(boxTop, boxLeft, headerWidth, this.periodColumnsInfo.c3.height, '#aaaaaa', '#aaaaaa'))
      objects.push(this.createLabel(periodsEntries[2][1].articleId, 'Period', '\n', labelTop, labelLeft, headerWidth, headerHeight, this.periodsHeaderProp.fontSize, this.periodsHeaderProp.fontWeight, 'center', '#000000', '#aaaaaa', true))
    }
    props.currentTopPosition += headerHeight
    this.remainingHeight -= headerHeight
    for (const columnIndex in props.groupByPeriodColumnTracker) {
      props.groupByPeriodColumnTracker[columnIndex].currentTopPosition += headerHeight
    }
    return objects
  }

  createVerticalPeriodDividerLines(modelPeriodsMap, currentTopPosition) {
    const linesObject: any[] = []
    const remainingHeight = this.slideHeight - currentTopPosition
    const reducedHeight = this.periodColumnsInfo.c2.dividerHeight - remainingHeight
    if (modelPeriodsMap.size === 2) {
      linesObject.push(this.createVerticalLine(this.periodColumnsInfo.c2.dividerTop - (remainingHeight / 2), this.periodColumnsInfo.c2.dividerLeft, reducedHeight))
    }
    else if (modelPeriodsMap.size === 3) {
      linesObject.push(this.createVerticalLine(this.periodColumnsInfo.c2.dividerTop - (remainingHeight / 2), this.periodColumnsInfo.c2.dividerLeft, reducedHeight))
      linesObject.push(this.createVerticalLine(this.periodColumnsInfo.c3.dividerTop - (remainingHeight / 2), this.periodColumnsInfo.c3.dividerLeft, reducedHeight))
    }
    return linesObject
  }

  createRectangle(top: number, left: number, width: number, height: number, backgroundColor = '#ffffff', border = '#ffffff') {
    return {
      type: 'rectangle',
      top,
      left,
      angle: 0,
      width,
      height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      fill: backgroundColor,
      stroke: border,
      locked: false,
      templateObject: false,
    }
  }

  createVerticalLine(top: number, left: number, height: number, strokeWidth = 1, color = '#000000') {
    return {
      type: 'line',
      top,
      left,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      stroke: color,
      strokeWidth,
      locked: true,
      width: height,
      height: 0,
      angle: 90,
      strokeDashArray: [7, 7],
      templateObject: true,
    }
  }

  /**
   * @description will create info table (colorway table) data for 1 article
   * @param {*} article
   * @param {*} props
   * @param {*} index
   */
  createInfoTableText(article: MyArticle, props: IProp) {
    const infoTableTextObjects: any[] = []
    let backgroundColor = '#FFFFFF'
    if (props.highlight === 1) {
      backgroundColor = '#DCDCDC'
      props.highlight = 0
    }
    else {
      props.highlight = 1
    }
    const dataFontWeight = 'normal'
    const dataFontSize = 10
    const colorwayWidth = utils.getTextWidth(article.Colorway ? article.Colorway.toString() : '', `${dataFontWeight}${dataFontSize}px${this.font}`)
    const dataHeight = Math.ceil(colorwayWidth / this.infoTableColorwayWidth) * 15
    const dataTop = props.infoTableTopPosition
    props.infoTableTopPosition = dataTop + dataHeight
    const colorwayCodeWidth = this.infoTableColorwayCodeWidth
    const colorwayCodeLeft = 5
    const colorwayCode = this.createLabel(
      article.Id,
      'ColorwayCode',
      '',
      dataTop,
      colorwayCodeLeft,
      colorwayCodeWidth,
      dataHeight,
      dataFontSize,
      dataFontWeight,
      'left',
      '#757575',
      backgroundColor,
      false,
    )
    infoTableTextObjects.push(colorwayCode)
    const colorWayNameWidth = this.infoTableColorwayWidth
    const colorWayNameLeft = colorwayCodeLeft + colorwayCodeWidth
    const colorWayName = this.createLabel(
      article.Id,
      'Colorway',
      '',
      dataTop,
      colorWayNameLeft,
      colorWayNameWidth,
      dataHeight,
      dataFontSize,
      dataFontWeight,
      'left',
      '#757575',
      backgroundColor,
      false,
    )
    infoTableTextObjects.push(colorWayName)
    const startShipWidth = this.infoTableStartShipWidth
    const startShipLeft = colorWayNameLeft + colorWayNameWidth
    const startShip = this.createLabel(
      article.Id,
      this.ShipmentStartDateAttribute,
      '',
      dataTop,
      startShipLeft,
      startShipWidth,
      dataHeight,
      dataFontSize,
      dataFontWeight,
      'left',
      '#757575',
      backgroundColor,
      false,
    )
    infoTableTextObjects.push(startShip)
    const hardLaunchDateWidth = this.infoTableHardLaunchDateWidth
    const hardLaunchDateLeft = startShipLeft + startShipWidth
    if (this.includeHardLaunchDate) {
      infoTableTextObjects.push(this.createLabel(
        article.Id,
        this.hardLaunchDateAttribute,
        '',
        dataTop,
        hardLaunchDateLeft,
        hardLaunchDateWidth,
        dataHeight,
        dataFontSize,
        dataFontWeight,
        'left',
        '#757575',
        backgroundColor,
        false,
      ))
    }
    const endShipWidth = this.infoTableEndShipWidth
    let endShipLeft = startShipLeft + startShipWidth
    if (this.includeHardLaunchDate) {
      endShipLeft = hardLaunchDateLeft + hardLaunchDateWidth
    }
    if (!this.includeUAStandardLaunchDateAttribute) {
      const endShip = this.createLabel(
        article.Id,
        this.ShipmentEndDateAttribute,
        '',
        dataTop,
        endShipLeft,
        endShipWidth,
        dataHeight,
        dataFontSize,
        dataFontWeight,
        'left',
        '#757575',
        backgroundColor,
        false,
      )
      infoTableTextObjects.push(endShip)
    }
    if (this.includeMarkdownDate) {
      const markdownDateWidth = this.infoTableMarkdownDateWidth
      const markdownDateLeft = endShipLeft + endShipWidth
      const markdownDate = this.createLabel(
        article.Id,
        'MarkdownDate',
        '',
        dataTop,
        markdownDateLeft,
        markdownDateWidth,
        dataHeight,
        dataFontSize,
        dataFontWeight,
        'left',
        '#757575',
        backgroundColor,
        false,
      )
      infoTableTextObjects.push(markdownDate)
    }
    return infoTableTextObjects
  }

  createPageHeaderObjects(articleId: number, title, subtitle) {
    const titleFontSize = 12
    const titleFontWeight = 'normal'
    const titleWidth = this.slideWidth - (this.logoWidth + 5) /* margin */ // assign width of page - logo width
    // const titleWidth = utils.gthis.slideWidthetTextWidth(title.value, `${titleFontWeight} ${titleFontSize}px ${this.font}`)
    const subTitleFontSize = 12
    const subTitleFontWeight = 'bold'
    const subTitleWidth = this.slideWidth - (this.logoWidth + 5) /* margin */ // assign width of page - logo width
    // const subTitleWidth = utils.getTextWidth(
    //   subtitle.value,
    //   `${subTitleFontWeight} ${subTitleFontSize}px ${this.font}`,
    // )
    const headerBg = {
      type: 'rectangle',
      top: 20,
      left: 640,
      angle: 0,
      width: 1280,
      height: this.headerHeight,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      fill: '#000000',
      stroke: '#000000',
      locked: false,
    }
    const logo = this.getHeaderLogo()
    const headerTitle = {
      type: 'articleDetails',
      articleId,
      top: 6,
      left: 5,
      angle: 0,
      width: titleWidth,
      height: this.labelHeightPerLine - 5,
      scaleX: 1,
      scaleY: 1,
      fill: '#FFFFFF',
      backgroundColor: '',
      flipX: false,
      flipY: false,
      customOptions: {
        articleProps: title.articleProperties,
        separator: '\n',
      },
      fontFamily: this.font,
      fontSize: titleFontSize,
      fontStyle: 'normal',
      fontWeight: titleFontWeight,
      textAlign: 'left',
      locked: false,
      isOriginTopLeft: true,
      templateObject: true,
    }
    const headerSubTitle = {
      type: 'articleDetails',
      articleId,
      top: 21,
      left: 5,
      angle: 0,
      width: subTitleWidth,
      height: this.labelHeightPerLine - 5,
      scaleX: 1,
      scaleY: 1,
      fill: '#FFFFFF',
      backgroundColor: '',
      flipX: false,
      flipY: false,
      customOptions: {
        articleProps: subtitle.articleProperties,
        separator: ' - ',
      },
      fontFamily: this.font,
      fontSize: subTitleFontSize,
      fontStyle: 'normal',
      fontWeight: subTitleFontWeight,
      textAlign: 'left',
      locked: false,
      isOriginTopLeft: true,
      templateObject: true,
    }
    return [headerBg, logo, headerTitle, headerSubTitle]
  }

  createSlideObjects(objects: any[], props: IProp, allSlides) {
    props.slideNumber++
    allSlides[props.slideNumber] = clone(objects)
  }

  // todo include templte object
  createTextObject(text: string, top: number, left: number, width: number, height: number, fontSize: number, fontWeight: string, textAlign: string, color: string, locked: boolean) {
    text = text != null ? text.toString() : ''
    // text = "•"
    return {
      type: 'textbox',
      top,
      left,
      width,
      height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      text,
      fill: color,
      backgroundColor: '',
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign,
      locked,
      isOriginTopLeft: true,
    }
  }

  getTextRequiredHeightBasedOnWidth(text: string, availableWidth: number, font: string, fontSize: number, fontWeight: string) {
    const minNumberOfLines = Math.ceil(utils.getTextWidth(text, `${fontWeight} ${fontSize}px ${font}`) / availableWidth)
    let multiLineSampleText = ''
    for (let index = 0; index < minNumberOfLines; index++) {
      multiLineSampleText += text
      if (index < minNumberOfLines - 1) {
        multiLineSampleText += '\n'
      }
    }
    return utils.getTextHeight(multiLineSampleText, fontSize, font, fabric)
  }

  addLine(top: number, left: number, angle: number, width: number, color = '#000000', strokeWidth = 1, templateObject = true, locked = true) {
    return {
      type: 'line',
      top,
      left,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      stroke: color,
      strokeWidth,
      strokeDashArray: null,
      locked,
      width,
      height: 1,
      angle,
      templateObject,
    }
  }

  // TODO- should not be hard coded
  getHeaderLogo() {
    return {
      type: 'image',
      top: 19.9,
      left: 1255.5,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 0,
      scaleX: 0.64,
      scaleY: 0.64,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data:
        'data:image/png;base64,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',
      // src: 'static/ua-logo.png'
    }
  }
}
interface IArticleData {
  articles: MyArticle[]
  articlePrices?: Record<string, number>
}
interface IGroupByPeriodColumnTracker {
  currentColumn: number
  currentTopPosition: number
  lowestThumbnailBottomPositionInModel: number // same usage as articleHeight
  pageFull: boolean
}
interface IProp {
  maxImagePerRow: number
  slideNumber: number
  currentRowMaxLabelHeight: number
  currentTopPosition: number // not in use when groupByPeriod is true
  infoTableTopPosition: number
  highlight: number
  articleName: string
  groupByPeriodColumnTracker: Record<number, IGroupByPeriodColumnTracker>
  groupByPeriodOverflowedArticles: any[]
  previousModelColumns: any[]
  articleIdList: number[]
}
export default new UAStandard()
